import { isModernAgentVersion } from '#xapp-core/foundation/composables/useAgentVersionState'
import { useNotificationState } from '#xapp-core/foundation/composables/useNotificationState'
import { createRouteContextRegistry } from '#xapp-core/mqtt/application/routeContextRegistry'
import { isLegacyMqttSupportEnabled } from '#xapp-core/mqtt/config/legacySupport'
import { applyClientPayload } from '#xapp-core/mqtt/domain/clientProjection'
import { adaptClientProjection } from '#xapp-core/mqtt/domain/clientProjectionAdapter'
import {
  adaptInboundResponse,
  adaptOutboundCommand,
} from '#xapp-core/mqtt/domain/commandCompatAdapter'
import { classifyMessage } from '#xapp-core/mqtt/domain/messageClassifier'
import { messageHandler } from '#xapp-core/mqtt/helpers/broker-dashboard'
import { createMqttTransportClient } from '#xapp-core/mqtt/infrastructure/mqttTransport'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import { markRaw, reactive, ref } from 'vue'

const DEFAULTS = {
  debug: false,
  LATENCY_WINDOW_SIZE: 20,
}
const CLIENT_UPDATE_BATCH_MS = 500

const debug = ref(false)

export const useMqttClientStore = defineStore('mqtt', {
  actions: {
    _applySubscriptionDiff(nextTopics) {
      for (const topic of this.subscribedTopics) {
        if (
          !nextTopics.has(topic) &&
          this._mqttClient &&
          this.status === 'Connected'
        ) {
          this._dlog('unsubscribing removed topic:', topic)
          this._mqttClient.unsubscribe(topic)
        }
      }

      for (const topic of nextTopics) {
        if (
          !this.subscribedTopics.has(topic) &&
          this._mqttClient &&
          this.status === 'Connected'
        ) {
          this._dlog('subscribing added topic:', topic)
          this._mqttClient.subscribe(topic)
        }
      }

      this.subscribedTopics = nextTopics
    },

    _bindAppExitHandlers() {
      if (this._appExitHandlersBound) {
        return
      }

      this._appExitDisconnectHandler = () => this.disconnectOnAppExit()
      window.addEventListener('beforeunload', this._appExitDisconnectHandler)
      window.addEventListener('pagehide', this._appExitDisconnectHandler)
      this._appExitHandlersBound = true
    },

    _connectSocket(brokerUrl, mqttOptions = {}) {
      this._isManuallyClosed = false
      this.status = 'Connecting'

      const { _, client } = createMqttTransportClient({
        brokerUrl,
        mqttOptions,
        onClose: () => this._onClose(),
        onConnect: () => this._onConnect(),
        onError: (error) => this._onError(error),
        onMessage: (topic, message) => this._onMessage(topic, message),
        onReconnect: () => this._onReconnect(),
      })

      this._mqttClient = client
      this._currentBrokerUrl = brokerUrl
    },

    _createClient(clientId) {
      const client = reactive({
        _messageFormat: 'legacy',
        _messageFormatLocked: false,
        _topicParts: markRaw([]), // markRaw is still good for array-heavy properties
        averageLatency: 0,
        baseTopic: '',
        id: clientId,
        inboundMessageCount: 0,
        isFirstLatencyRecorded: false,
        lastMessage: null,
        lastSeen: null,
        latencyHistory: markRaw([]),
        maxLatency: null,
        messageCount: 0,
        minLatency: null,
        outboundMessageCount: 0,
      })

      this.clients.set(clientId, client)
      this._clientUpdateTracker++
      return client
    },

    _deriveDefaultClientPatterns(subscribedTopics) {
      const keys = new Set()
      const candidates = []

      for (const topic of subscribedTopics) {
        const normalized = String(topic || '')
        if (!normalized) continue
        if (normalized.startsWith('$SYS/')) continue
        if (normalized.includes('/resp/')) continue

        const parts = normalized.split('/')
        if (parts.length < 3) continue

        const clientIndex = parts.length - 2
        const clientCandidate = parts[clientIndex]
        if (
          !clientCandidate ||
          clientCandidate === '#' ||
          clientCandidate === '+' ||
          clientCandidate === 'cmd' ||
          clientCandidate === 'resp'
        ) {
          continue
        }

        const prefix = parts.slice(0, clientIndex).join('/')
        if (!prefix) continue
        const key = `${prefix}::${clientIndex}`
        keys.add(key)
        candidates.push({ clientIndex, key, prefix })
      }

      if (keys.size === 1 && candidates.length > 0) {
        const { clientIndex, prefix } = candidates[0]
        return [{ clientIndex, prefix }]
      }

      if (keys.size > 1) {
        this._dlog('default clientPatterns ambiguous; keeping empty', {
          keys: Array.from(keys),
        })
      }

      return []
    },

    // --- Helper Actions ---
    _dlog(...args) {
      if (debug.value) {
        console.debug('[MqttStore]', ...args)
      }
    },

    _incrementClientOutboundMessageCount(clientId) {
      if (!clientId) {
        return
      }

      if (!this.clients.has(clientId)) {
        this._createClient(clientId)
      }

      const client = this.clients.get(clientId)
      client.outboundMessageCount = (client.outboundMessageCount || 0) + 1
      this._clientUpdateTracker++
    },

    _onClose() {
      if (this._isManuallyClosed) {
        this.status = 'Disconnected'
      }
    },

    _onConnect() {
      this.status = 'Connected'
      this._dlog('connected')

      for (const topic of this.subscribedTopics) {
        this._dlog('Auto-subscribe to topic:', topic)
        this._mqttClient.subscribe(topic)
      }
    },

    _onError(error) {
      this.status = 'Connection Error'
      console.error('MQTT error:', error)
    },

    _onGeneralMessageDefault(_topic, payload) {
      if (!payload?.status) {
        return
      }

      const { notify } = useNotificationState()

      notify({
        color: String(payload.status).toLowerCase(),
        text: String(payload?.message || ''),
      })
    },

    _onMessage(topic, message) {
      this.lastMessageTimestamp = new Date()
      const classified = classifyMessage({
        clientPatterns: this._clientPatterns,
        message,
        topic,
      })

      if (classified.kind === 'system') {
        this._processSystemMessage(classified.topic, classified.message)
        return
      }

      if (classified.kind === 'general') {
        if (this._onGeneralMessageHooks.length > 0) {
          for (const handler of this._onGeneralMessageHooks) {
            try {
              handler(classified.topic, classified.payload)
            } catch (error) {
              console.error('[MqttStore] onGeneralMessageHook error:', error)
            }
          }
        }
        return
      }

      if (isLegacyMqttSupportEnabled()) {
        const adaptedResponse = adaptInboundResponse({
          payload: classified.payload,
          topic: classified.topic,
        })
        if (adaptedResponse) {
          if (this._onGeneralMessageHooks.length > 0) {
            for (const handler of this._onGeneralMessageHooks) {
              try {
                handler(adaptedResponse.topic, adaptedResponse.payload)
              } catch (error) {
                console.error('[MqttStore] onGeneralMessageHook error:', error)
              }
            }
          }
          return
        }
      }

      if (classified.kind === 'client') {
        const clientObj = this._processClientMessage(
          classified.topic,
          classified.payload,
          {
            clientId: classified.clientId,
            propertyParts: classified.propertyParts,
          }
        )
        if (clientObj && this._onAfterMessageHook) {
          try {
            this._onAfterMessageHook(clientObj, topic, classified.payload)
          } catch (error) {
            console.error('[MqttStore] onAfterMessageHook error:', error)
          }
        }
        return
      }

      if (DEFAULTS.debug) {
        this._dlog('no pattern match', { topic })
      }
    },

    _onReconnect() {
      this.status = 'Reconnecting'
    },

    _processClientMessage(topic, payload, match) {
      const { clientId, propertyParts } = match
      if (!clientId) {
        return null
      }

      if (!this.clients.has(clientId)) {
        this._createClient(clientId)
        this._dlog(`created client '${clientId}'`)
      }

      const client = this.clients.get(clientId)
      const receivedTimestamp = new Date()
      const normalized = isLegacyMqttSupportEnabled()
        ? adaptClientProjection({
            clientId,
            payload,
            propertyParts,
            topic,
          })
        : {
            format: 'current',
            writes: [{ payload, propertyParts }],
          }
      const writes = Array.isArray(normalized?.writes) ? normalized.writes : []
      let sawAgentWrite = false
      for (let i = 0; i < writes.length; i++) {
        const write = writes[i]
        if (
          Array.isArray(write?.propertyParts) &&
          write.propertyParts[0] === 'agent'
        ) {
          sawAgentWrite = true
        }
        applyClientPayload({
          client,
          latencyWindowSize: DEFAULTS.LATENCY_WINDOW_SIZE,
          payload: write?.payload,
          propertyParts: write?.propertyParts,
          receivedTimestamp,
          skipLatency: i > 0,
          topic,
        })
      }
      const normalizedTopic = String(topic || '').toLowerCase()
      if (
        client._messageFormatLocked !== true &&
        (sawAgentWrite || normalizedTopic.includes('xapp-agent'))
      ) {
        const normalizedVersion = String(
          client?.agent?.version || payload?.version || ''
        ).trim()
        const hasSemverVersion = /^(\d+)\.(\d+)\.(\d+)/.test(normalizedVersion)
        const isLegacyFormat =
          normalizedTopic.includes('xapp-agent') &&
          hasSemverVersion &&
          !isModernAgentVersion(normalizedVersion)
        client._messageFormat = isLegacyFormat ? 'legacy' : 'current'
        client._messageFormatLocked = true
      }
      this._scheduleClientUpdate()
      return client
    },

    // -------------------------------------------------------------------------
    // MQTT Event Handlers
    // -------------------------------------------------------------------------

    _processSystemMessage(topic, message) {
      messageHandler(topic, message, { broker: this.broker })
    },

    _rebuildRouteDerivedState() {
      const nextState = this._routeContextRegistry.compute()
      const handlers = Array.isArray(nextState.generalHandlers)
        ? [...nextState.generalHandlers]
        : []
      if (!handlers.includes(this._onGeneralMessageDefault)) {
        handlers.push(this._onGeneralMessageDefault)
      }
      this._onGeneralMessageHooks = handlers
      this._clientPatterns =
        Array.isArray(nextState.clientPatterns) &&
        nextState.clientPatterns.length > 0
          ? nextState.clientPatterns
          : this._deriveDefaultClientPatterns(nextState.subscribedTopics)

      const nextTopics = nextState.subscribedTopics
      this._applySubscriptionDiff(nextTopics)
      this._dlog('effective route state', {
        contexts: nextState.contextCount,
        generalHandlers: this._onGeneralMessageHooks.length,
        patterns: this._clientPatterns.length,
        subscriptions: this.subscribedTopics.size,
      })
    },

    _scheduleClientUpdate() {
      if (this._clientUpdateTimer !== null) {
        return
      }

      this._clientUpdateTimer = setTimeout(() => {
        this._clientUpdateTimer = null
        this._clientUpdateTracker++
      }, CLIENT_UPDATE_BATCH_MS)
    },

    // Add a new general message handler
    addGeneralMessageHandler(handler) {
      if (typeof handler === 'function') {
        if (this._routeContextRegistry.addManualGeneralHandler(handler)) {
          this._rebuildRouteDerivedState()
          this._dlog(
            'Added general message handler. Total handlers:',
            this._onGeneralMessageHooks.length
          )
        }
      } else {
        console.error('Handler must be a function')
      }
    },

    clearSubscribedTopics() {
      console.log('Clearing subscribed topics')
      this._routeContextRegistry.clearManualTopics()
      this._rebuildRouteDerivedState()
    },

    connectPersistent(brokerUrl, mqttOptions = {}) {
      if (this._mqttClient && this._currentBrokerUrl === brokerUrl) {
        return
      }

      if (this._mqttClient && this._currentBrokerUrl !== brokerUrl) {
        this._isManuallyClosed = true
        this._mqttClient.end(true)
        this._mqttClient = null
        this.status = 'Idle'
      }

      const envUsername = import.meta.env.VITE_MQTT_USERNAME
      const envPassword = import.meta.env.VITE_MQTT_PASSWORD
      const authOptions = {}

      if (!mqttOptions.username && envUsername) {
        authOptions.username = envUsername
      }

      if (!mqttOptions.password && envPassword) {
        authOptions.password = envPassword
      }

      this._bindAppExitHandlers()
      this._connectSocket(brokerUrl, {
        clientId: mqttOptions.clientId || uuidv4(),
        ...authOptions,
        ...mqttOptions,
      })
    },

    // -------------------------------------------------------------------------
    // Public Methods
    // -------------------------------------------------------------------------

    disconnect() {
      if (!this._mqttClient) {
        return
      }
      this._dlog('disconnecting')

      this._isManuallyClosed = true
      this._mqttClient.end(true)
      this._mqttClient = null
      this._currentBrokerUrl = null
      this.status = 'Idle'
      this.resetState()
    },

    disconnectOnAppExit() {
      this.disconnect()
    },

    publish(topic, payload, publishOptions = {}) {
      if (this._mqttClient && this.status === 'Connected') {
        const msg =
          typeof payload === 'object'
            ? JSON.stringify(payload)
            : String(payload)
        this._mqttClient.publish(topic, msg, publishOptions)
      } else {
        console.error('Cannot publish: MQTT client not connected.')
      }
    },

    publishCommand({
      action,
      baseTopic,
      data = {},
      subscribeResponse = true,
      type,
    }) {
      if (!this._mqttClient?.options?.clientId) {
        console.error('Cannot publish command: MQTT client not connected.')
        return
      }

      // Publish with correlation ID and reply-to topic for tracking
      const MQTT_CLIENT_ID = this._mqttClient.options.clientId
      const clientId =
        String(baseTopic || '')
          .split('/')
          .at(-1) || ''
      const clientFormat = String(
        this.clients instanceof Map
          ? this.clients.get(clientId)?._messageFormat || 'current'
          : 'current'
      )
      if (isLegacyMqttSupportEnabled()) {
        const legacyCommand = adaptOutboundCommand({
          action,
          baseTopic,
          clientFormat,
          data,
          type,
        })
        if (legacyCommand?.topic) {
          this._incrementClientOutboundMessageCount(clientId)
          this.publish(legacyCommand.topic, legacyCommand.payload, {
            qos: 1,
            retain: false,
          })
          return
        }
      }

      const topic = `${baseTopic}/cmd/${type}/${action}`
      console.log(topic)
      const reply_to_topic = `${baseTopic}/resp/${type}/${action}/${MQTT_CLIENT_ID}`
      // this._dlog('Publishing to topic:', topic)
      if (subscribeResponse) {
        this._dlog('Response topic:', reply_to_topic)
        this.subscribe(reply_to_topic)
      }
      const payload = {
        client_id: MQTT_CLIENT_ID,
        correlation_id: uuidv4(),
        data,
        reply_to_topic,
      }
      this._incrementClientOutboundMessageCount(clientId)
      this.publish(topic, payload, { qos: 1, retain: false })
    },

    registerRouteContext({
      clientPatterns = [],
      contextId,
      onGeneralMessage,
      subscriptions = [],
    }) {
      if (!contextId) {
        console.error('registerRouteContext requires contextId')
        return
      }

      this._routeContextRegistry.registerContext({
        clientPatterns: Array.isArray(clientPatterns) ? clientPatterns : [],
        contextId,
        onGeneralMessage:
          typeof onGeneralMessage === 'function' ? onGeneralMessage : null,
        subscriptions: Array.isArray(subscriptions) ? subscriptions : [],
      })
      this._dlog('registered route context', {
        contextId,
        patterns: Array.isArray(clientPatterns) ? clientPatterns.length : 0,
        subscriptions: Array.isArray(subscriptions) ? subscriptions.length : 0,
      })
      this._rebuildRouteDerivedState()
    },

    // Utility actions for external use
    removeClient(clientId) {
      if (this.clients[clientId]) {
        delete this.clients[clientId]
      }
    },

    // Remove a general message handler
    removeGeneralMessageHandler(handler) {
      const removed =
        this._routeContextRegistry.removeManualGeneralHandler(handler)
      if (!removed) {
        console.warn('Handler not found')
      } else {
        this._rebuildRouteDerivedState()
        this._dlog(
          'Removed a general message handler. Total handlers:',
          this._onGeneralMessageHooks.length
        )
      }
    },

    resetState() {
      this._dlog('resetting state')
      if (this._clientUpdateTimer !== null) {
        clearTimeout(this._clientUpdateTimer)
        this._clientUpdateTimer = null
      }
      this.clients.clear()

      this._clientUpdateTracker++

      this.subscribedTopics.clear()
      this._onAfterMessageHook = null
      this._onGeneralMessageHooks = []
      this._clientPatterns = []
      this._routeContextRegistry.reset()
    },

    setDebug(value) {
      debug.value = value
      this._dlog('Debug mode set to:', value)
    },

    subscribe(topic) {
      const added = this._routeContextRegistry.addManualTopic(topic)
      if (!added) {
        // this._dlog('Already subscribed to topic:', topic)
        return
      }
      this._rebuildRouteDerivedState()
    },

    unregisterRouteContext(contextId) {
      if (!contextId) {
        return
      }
      this._routeContextRegistry.removeContext(contextId)
      this._dlog('unregistered route context', { contextId })
      this._rebuildRouteDerivedState()
    },

    unsubscribe(topic) {
      this._routeContextRegistry.removeManualTopic(topic)
      this._rebuildRouteDerivedState()
    },
  },

  getters: {
    clientCount: (state) => state.clients.size,
    getClientById: (state) => (clientId) => {
      // Accessing the raw map to get the raw client object
      const client = state.clients.get(clientId)
      // We still return the object/proxy, but the map itself isn't proxied
      return client
    },
    isConnected: (state) => state.status === 'Connected',

    isConnecting: (state) => state.status === 'Connecting',
  },

  state: () => ({
    _appExitDisconnectHandler: null,
    _appExitHandlersBound: false,
    _clientPatterns: [],

    _clientUpdateTimer: null,
    _clientUpdateTracker: 0,
    _currentBrokerUrl: null,
    _deprecatedConnectOptionsWarned: false,

    _isManuallyClosed: false,
    // Internal tracking
    _mqttClient: null,

    _onAfterMessageHook: null,
    _onGeneralMessageHooks: [],
    _routeContextRegistry: markRaw(createRouteContextRegistry()),
    // Broker stats
    broker: {
      bytes: { received: null, sent: null },
      clients: { connected: null, disconnected: null, total: null },
      messages: { received: null, sent: null },
      publish: {
        bytes: { received: null, sent: null },
        messages: { dropped: null, sent: null },
      },
      retained_messages: { count: null },
      store: { messages: { bytes: null, count: null } },
      subscriptions: { count: null },
      uptime: null,
      version: null,
    },
    // Client tracking
    clients: markRaw(new Map()),
    lastMessageTimestamp: null,
    // Connection state
    status: 'Idle',

    // Private state (not reactive, but stored in state for persistence)
    subscribedTopics: new Set(),
  }),
})
